import { useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Alert, InputAdornment, IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff, LockReset } from '@mui/icons-material';
import { useAppDispatch, useAppSelector } from '../../hooks/useAppStore';
import { updateUser, logout } from './authSlice';
import { baseApi } from '../../store/api/baseApi';
import { useChangePasswordMutation } from '../../store/api/usersApi';

/**
 * Pantalla bloqueante de primer ingreso: cuando el agente tiene `mustChangePassword`
 * (clave temporal cargada en el relanzamiento), el CRM no lo deja entrar hasta que
 * cambie la contraseña. Al cambiarla, el backend limpia el flag y desbloqueamos.
 */
export function ForcePasswordChange() {
  const dispatch = useAppDispatch();
  const user = useAppSelector((s) => s.auth.user);
  const [changePassword, { isLoading }] = useChangePasswordMutation();

  const [current, setCurrent] = useState('');
  const [next, setNext]       = useState('');
  const [confirm, setConfirm] = useState('');
  const [show, setShow]       = useState(false);
  const [error, setError]     = useState('');

  const handleSubmit = async () => {
    setError('');
    if (next !== confirm)      { setError('Las contraseñas nuevas no coinciden'); return; }
    if (next.length < 8)       { setError('La nueva contraseña debe tener al menos 8 caracteres'); return; }
    if (next === current)      { setError('La nueva contraseña debe ser distinta a la temporal'); return; }
    if (!user)                 return;
    try {
      await changePassword({ id: user._id, currentPassword: current, newPassword: next }).unwrap();
      // El backend ya puso mustChangePassword=false; reflejamos en el store para desbloquear.
      dispatch(updateUser({ mustChangePassword: false }));
    } catch (e: any) {
      setError(e?.data?.message ?? 'No pudimos cambiar la contraseña. Revisá la actual.');
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center',
        p: 2, bgcolor: 'background.default',
      }}
    >
      <Paper elevation={3} sx={{ p: { xs: 3, sm: 4 }, width: '100%', maxWidth: 440, borderRadius: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
          <Box sx={{
            width: 56, height: 56, borderRadius: '50%', bgcolor: 'primary.main',
            display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5,
          }}>
            <LockReset sx={{ color: 'primary.contrastText', fontSize: 30 }} />
          </Box>
          <Typography variant="h6" fontWeight={700} textAlign="center">
            Cambiá tu contraseña
          </Typography>
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 0.5 }}>
            Por seguridad, en tu primer ingreso tenés que reemplazar la contraseña temporal.
          </Typography>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <TextField
          fullWidth label="Contraseña temporal (la actual)" type={show ? 'text' : 'password'}
          value={current} onChange={(e) => setCurrent(e.target.value)} margin="dense" autoFocus
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton onClick={() => setShow((s) => !s)} edge="end" size="small">
                  {show ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        <TextField
          fullWidth label="Nueva contraseña" type={show ? 'text' : 'password'}
          value={next} onChange={(e) => setNext(e.target.value)} margin="dense"
          helperText="Mínimo 8 caracteres"
        />
        <TextField
          fullWidth label="Repetí la nueva contraseña" type={show ? 'text' : 'password'}
          value={confirm} onChange={(e) => setConfirm(e.target.value)} margin="dense"
          onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
        />

        <Button
          fullWidth variant="contained" size="large" sx={{ mt: 2.5, py: 1.2, fontWeight: 700 }}
          onClick={handleSubmit} disabled={isLoading || !current || !next || !confirm}
        >
          {isLoading ? 'Guardando…' : 'Guardar y entrar'}
        </Button>
        <Button
          fullWidth variant="text" size="small" sx={{ mt: 1 }}
          onClick={() => { dispatch(logout()); dispatch(baseApi.util.resetApiState()); }}
        >
          Cerrar sesión
        </Button>
      </Paper>
    </Box>
  );
}
